<template lang="pug">
v-dialog(v-model="show" persistent max-width="600px" :fullscreen="$vuetify.breakpoint.smAndDown")
    v-card.d-flex.flex-column
        v-card-title.headline {{$t('boarderGroupEditorDialog.dialogTitle')}}
        v-card-text.pa-0.flex-shrink-0.flex-grow-1.card-body
            v-form(ref="form" v-model="isFormValid" :lazy-validation="true" :disabled="isSaveInProgress")
                v-container.pa-0
                    v-tabs(v-model="currentTab")
                        v-tab {{$t('boarderGroupEditorDialog.propertiesTabLabel')}}
                        v-tab {{$t('boarderGroupEditorDialog.pricesTabLabel')}}


                    v-tabs-items(v-model="currentTab")
                        v-tab-item
                            v-container
                                v-row
                                    v-col
                                        v-text-field(
                                            v-model="currentName"
                                            label="Názov skupiny *" outlined required
                                        :dense="$vuetify.breakpoint.mdAndUp" :rules="nameRules"
                                        )
                                v-row
                                    v-col
                                        v-select(
                                            v-model="selectedTagIds"
                                            :items="tagList"
                                            :label="$t('boarderGroupEditorDialog.tagsLabel')"
                                            item-text="title"
                                            item-value="id"
                                            multiple chips outlined
                                            :dense="$vuetify.breakpoint.mdAndUp"
                                            append-icon="fas fa-chevron-down"
                                            :loading="tagsFetchInProgress"
                                        )
                                            template(v-slot:selection="{ item }")
                                                v-chip(small)
                                                    v-icon.mr-2(small left v-if="item.iconClass") {{tagIcon(item.iconClass)}}
                                                    span {{item.title}}
                                            template(v-slot:item="{ active, item, attrs, on }")
                                                v-list-item(v-slot:default="{ active }" v-on="on" v-bind="attrs")
                                                    v-list-item-action
                                                        v-checkbox(:input-value="active" on-icon="fas fa-check-square" off-icon="far fa-square")
                                                    v-list-item-content
                                                        v-list-item-title
                                                            v-icon.mr-4(v-if="item.iconClass") {{tagIcon(item.iconClass)}}
                                                            span {{item.title}}

                        v-tab-item
                            v-container(v-for="(menuGroupItem, idx) in  menuGroupList" :key="idx")
                                v-card
                                    v-card-title.subtitle-1 {{menuGroupItem.name}}
                                    v-card-text
                                      v-row
                                          v-col.pb-0(cols="6")
                                          v-col.pb-0(cols="6")
                                              v-select.ma-0(
                                                v-model="selectedAlgorithm"
                                                :label="$t('boarderGroupEditorDialog.algorithmLabel')"
                                                outlined dense
                                                :items="fetchAlgorithmList"
                                                item-value="id"
                                                item-text="name"
                                                :rules="referenceAlgorithmRules"
                                                append-icon="fas fa-chevron-down"
                                                :loading="fetchInProgress")
                                      v-row(v-for="(menuGroupSlot, idx) in menuGroupSlots(menuGroupItem)" :key="idx")
                                          v-col.d-flex.align-center.justify-end(cols="6" :class="idx===0 ? 'pt-0' : '' ") {{menuGroupSlot.name}} ({{menuGroupSlot.code}})
                                          v-col(cols="6" :class="idx===0 ? 'pt-0' : '' ")
                                              v-text-field(
                                                  dense outlined hide-details append-icon="fal fa-euro-sign" :label="$t('boarderGroupEditorDialog.priceFieldLabel')"
                                                  :rules="referencePriceRules" v-model="slotPrices[menuGroupSlot.id]"
                                              )
        v-divider
        v-card-actions
            v-spacer
            v-btn(
                color="primary" @click="onSubmitButtonClick" :disabled="!isFormValid" :loading="isSaveInProgress"
            ) {{$t('boarderGroupEditorDialog.saveButtonLabel')}}
            v-btn(color="primary" text :disabled="isSaveInProgress" @click="onCloseButtonClick") {{$t('boarderGroupEditorDialog.cancelButtonLabel')}}
</template>

<script lang="ts">
import Vue from 'vue'
import {Component, Prop, Ref,} from 'vue-property-decorator'
import {Action} from 'vuex-class'
import type {BoarderGroupResponseModel} from "@/model/boarder/BoarderGroup";
import {TranslateResult} from "vue-i18n";
import i18n from "@/i18n";
import DailyMenuOption from "@/model/dailymenu/DailyMenuOption";
import {BOARDER_UPDATE_BOARDER_GROUP, FETCH_ALGORITHM_LIST, FETCH_DAILY_MENU, FETCH_TAGS} from "@/store/action-types";
import MenuOffer from "@/model/dailymenu/MenuOffer";
import moment from "moment";
import _ from "lodash";
import MenuGroupItem from "@/model/dailymenu/MenuGroupItem";
import {AlgorithmList} from "@/model/AlgorithmList";
import {BoarderGroupPayload} from "@/types/payload";
import Tag from "@/model/tennant/Tag";
import {tagIconClass} from "@/services/tagIcon";

function valueIsRequired(v: string): boolean | TranslateResult {
  return !!v || i18n.t('validation.valueIsRequired')
}

function valueMustBeNumber(v: any): boolean | TranslateResult {
  if (v) {
    v = v.replace(/,/, '.')

    const result = !isNaN(v - Number.parseFloat(v));

    if (!result) {
      return i18n.t('validation.valueMustBeNumber')
    }
  }

  return true
}

function valueMustBePositiveNumber(v: string): boolean | TranslateResult {
  if (v) {
    const parsed = parseFloat(v)

    if (parsed <= 0) {
      return i18n.t('validation.valueMustBePositiveNumber')
    }
  }

  return true
}

@Component
export default class BoarderGroupEditorDialog extends Vue {
  private menuGroupList: MenuGroupItem[] = []
  private isFormValid: boolean = true
  private isSaveInProgress: boolean = false
  private currentName: string = ''
  private currentTab: number = 1
  private slotPrices: { [key: number]: string } = {}
  private selectedAlgorithm: number | null = null
  private fetchAlgorithmList: AlgorithmList[] = []
  private fetchInProgress: boolean = false
  private tagList: Tag[] = []
  private selectedTagIds: number[] = []
  private tagsFetchInProgress: boolean = false

  private nameRules: Function[] = [
    valueIsRequired
  ]

  private referencePriceRules: Function[] = [
    valueMustBeNumber,
    valueMustBePositiveNumber
  ]

  private referenceAlgorithmRules: Function[] = [
      valueIsRequired
  ]

  @Prop({type: Boolean, default: false}) show!: boolean
  @Prop({type: Object, required: true}) boarderGroup!: BoarderGroupResponseModel
  @Prop({type: Number}) priceAlgorithmId!: number

  @Ref('form') readonly form!: Vue & { validate(): boolean }

  @Action(FETCH_DAILY_MENU) fetchMenuStructure!: ({ (payload: { from: string, to: string }): Promise<MenuOffer> })
  @Action(BOARDER_UPDATE_BOARDER_GROUP) updateBoarderGroup!: { (payload: BoarderGroupPayload): Promise<BoarderGroupResponseModel> }
  @Action(FETCH_ALGORITHM_LIST) fetchAlgorithm!: ({ (): Promise<AlgorithmList[]>})
  @Action(FETCH_TAGS) fetchTagList!: ({ (): Promise<Tag[]> })

  tagIcon(iconClass: string | null): string {
    return tagIconClass(iconClass)
  }

  menuGroupSlots(item: MenuGroupItem): DailyMenuOption[] {
    let list: DailyMenuOption[] = []

    item.subGroupList.forEach((sg: MenuGroupItem) => {
      list = list.concat(sg.optionList)
    });

    return list
  }

  onSubmitButtonClick(): void {
    if (this.form.validate()) {
      this.save()
    }
  }

  onCloseButtonClick(): void {
    this.$emit('close')
  }

  async fetchData(): Promise<void> {
    const today = moment().format('YYYY-MM-DD')

    const offer = await this.fetchMenuStructure({
      from: today,
      to: today
    })

    this.menuGroupList = _.first(offer.menuList)!.menuGroupList
  }

  async fetchAlgorithmData(): Promise<void> {
    try {
      this.fetchInProgress = true
      this.fetchAlgorithmList = await this.fetchAlgorithm()
    }catch (e) {

    }finally {
      this.fetchInProgress = false
    }
  }

  async fetchTagData(): Promise<void> {
    try {
      this.tagsFetchInProgress = true
      this.tagList = await this.fetchTagList()
    } catch (e) {

    } finally {
      this.tagsFetchInProgress = false
    }
  }

  async save(): Promise<void> {
    const boarderGroupItem: BoarderGroupPayload = {
      id: this.boarderGroup.id,
      name: this.currentName,
      tagIds: this.selectedTagIds,
      businessCustomer: this.boarderGroup.businessCustomer,
      slotPrices: Object.keys(this.slotPrices)
        .filter((slotId: any) => !!this.slotPrices[slotId])
        .map((slotId: any) => {
          const price = this.slotPrices[slotId].replace(/,/, '.')

          return {
            menuGroupSlot: {
              id: Number.parseInt(slotId)
            },
            price: Number.parseFloat(price)
          }
        }),
      priceAlgorithmId: this.selectedAlgorithm!
    }

    this.isSaveInProgress = true

    try {
      const result = await this.updateBoarderGroup(boarderGroupItem)

      this.$emit('boarderGroupSave', result)
    } finally {
      this.isSaveInProgress = false
    }
  }

  mounted(): void {
    this.currentName = this.boarderGroup.name
    this.boarderGroup.slotPrices.forEach(sp => this.slotPrices[sp!.menuGroupSlot.id] = sp!.price.toFixed(2))
    this.selectedAlgorithm = this.priceAlgorithmId
    this.selectedTagIds = (this.boarderGroup.tags || []).map(tag => tag.id)
    this.fetchData()
    this.fetchAlgorithmData()
    this.fetchTagData()
  }
}
</script>

<style lang="stylus" scoped>
.card-body
  overflow auto
</style>
